/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import React, {
  createContext,
  ReactNode,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef
} from 'react'
import {
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  browserLocalPersistence,
  User
} from 'firebase/auth'
import {
  isTokenExpired,
  notifyError,
  notifySuccess,
  requestNotificationPermission
} from 'utils'

import {
  useGetUserDetailsQuery,
  useSaveUserDetailsMutation,
  useUpdateSyncUserDetailsMutation
} from 'pages/profile/profileApiSlice'
import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore'
import { userDetailsResponse } from 'pages/profile/types'
import { auth } from '../config/firebase'

interface AuthContextType {
  fetchUserRole(): unknown
  // eslint-disable-next-line prettier/prettier
  user: User | null;
  error :any;
  loginUserDetails: userDetailsResponse | null;
  token: string | null;
  role: string[] | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  fetchUserDetails: () => void;
  refreshToken: () => void;
  loading: boolean;
  loadingRole: boolean;
  isSubsId: boolean;
  isCompanyVerified: boolean;
  isUserSynced: boolean;
  openEditProfile: boolean;
  errorCompanyDetails:boolean;
  setopenEditProfile: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSubsId: React.Dispatch<React.SetStateAction<boolean>>;
  syncUserWithDatabase: (currentUser: User) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [loginUserDetails, setLoginUserDetails] =
    useState<userDetailsResponse | null>(null)
    const[errorCompanyDetails,setErrorCompanyDetails]=useState<any>(null)
  const [loadingRole, setLoadingRole] = useState<boolean>(true)
  const [lastActivityTime, setLastActivityTime] = useState<number>(Date.now())
  const [isSubsId, setIsSubsId] = useState<boolean>(false)
  const [isCompanyVerified, setIsCompanyVerified] = useState<boolean>(false)
  const [isUserSynced, setIsUserSynced] = useState<boolean>(false)
  const [openEditProfile, setopenEditProfile] = useState<boolean>(false)
  const [role, setRole] = useState<string[] | null>(() => {
    const storedRole = localStorage.getItem('role')
    return storedRole ? JSON.parse(storedRole) : null
  })

  const [token, setToken] = useState<string | null>(
    localStorage.getItem('token')
  )

  const logoutCalledRef = useRef(false)
  const isLoggingIn = useRef(false)
  const [saveUserDetails] = useSaveUserDetailsMutation()
  const [updateSyncUserDetails] = useUpdateSyncUserDetailsMutation()
  const {
    data: userDetail,
    error: userDetailError,
    refetch: fetchUserDetails
  } = useGetUserDetailsQuery(undefined, { skip: !token })

  useEffect(() => {
    if (userDetail) {
      const roles = userDetail?.authorities
        ? userDetail?.authorities?.map((item: { name: string }) => item.name)
        : null
      setRole(roles)
      setLoginUserDetails(userDetail)
      const subsId = userDetail?.companyDTO?.subsId ?? null
      const active = userDetail?.companyDTO?.active ?? false
      setIsSubsId(!!subsId)
      setIsCompanyVerified(!!active)
      localStorage.setItem('role', JSON.stringify(roles))
      setLoadingRole(false)
    } else if (userDetailError) {
      console.error('Error while fetching user details:', userDetailError)
      setRole(null)
      setIsSubsId(true)
      localStorage.setItem('role', JSON.stringify(null))
      setLoadingRole(false)
      setErrorCompanyDetails(true)
    }
  }, [userDetail, userDetailError])

  const logout = useCallback(async () => {
    if (logoutCalledRef.current) return
    logoutCalledRef.current = true

    try {
      await signOut(auth)
      setUser(null)
      setRole(null)
      setToken(null)
      setIsUserSynced(false)
      localStorage.clear()
      notifySuccess('Logout successful')
    } catch (error) {
      console.error('Logout error:', error)
      notifyError('Logout failed')
    } finally {
      logoutCalledRef.current = false
    }
  }, [])

  const refreshToken = useCallback(async () => {
    if (!auth.currentUser) return
    try {
      const refreshedToken = await auth.currentUser.getIdToken(true)
      if (isTokenExpired(refreshedToken)) {
        notifyError('Session expired, please log in again')
        logout()
      } else {
        localStorage.setItem('token', refreshedToken)
        setToken(refreshedToken)
      }
    } catch (error) {
      console.error('Error refreshing token:', error)
      notifyError('Error refreshing token')
      logout()
    }
  }, [logout])

  useEffect(() => {
    const checkInactivity = () => {
      const currentTime = Date.now()
      const timeSinceLastAction = currentTime - lastActivityTime

      if (timeSinceLastAction >= 60 * 60 * 1000) {
        notifyError('Session expired due to inactivity.')
        logout()
      } else if (
        timeSinceLastAction >= 30 * 60 * 1000 &&
        timeSinceLastAction < 59 * 60 * 1000
      ) {
        refreshToken()
      }
    }

    const interval = setInterval(checkInactivity, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [lastActivityTime, logout, refreshToken])

  useEffect(() => {
    const resetTimer = () => setLastActivityTime(Date.now())

    ;['mousemove', 'keydown', 'click', 'scroll'].forEach((event) =>
      window.addEventListener(event, resetTimer)
    )

    return () => {
      ;['mousemove', 'keydown', 'click', 'scroll'].forEach((event) =>
        window.removeEventListener(event, resetTimer)
      )
    }
  }, [])
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true)
      if (currentUser) {
        const userToken = await currentUser.getIdToken(true)
        if (isTokenExpired(userToken)) {
          notifyError('Session expired, please log in again')
          logout()
        } else {
          localStorage.setItem('token', userToken)
          setToken(userToken)
          setUser(currentUser)
          fetchUserDetails()
        }
      } else {
        setUser(null)
        localStorage.clear()
      }
      setLoading(false)
    })

    auth.setPersistence(browserLocalPersistence).catch((error) => {
      console.error('Persistence failed:', error)
      setLoading(false)
      setLoadingRole(false)
    })

    return () => unsubscribe()
  }, [fetchUserDetails, logout])

  const syncUserWithDatabase = async (currentUser: User) => {
    if (isUserSynced) return
    setIsUserSynced(true)
    const FCMtoken = await requestNotificationPermission()
    const payload = {
      fcmToken: FCMtoken,
      email: currentUser?.email,
      mobileNumber: currentUser?.phoneNumber,
      firstname: currentUser?.displayName,
      firebaseId: currentUser?.uid,
      type: 'AFC'
    }

    const userToken = localStorage.getItem('token')
    if (!userToken) {
      console.error('User token is null')
      setIsUserSynced(false)
      return
    }
    try {
      await saveUserDetails({
        payload,
        token: userToken
      }).unwrap()
    } catch (error) {
      console.error('error occured', error)
      if ((error as any).status === 400) {
        const updatePayload = {
          fcmToken: FCMtoken,
          email: currentUser?.email,
          firstname: currentUser?.displayName,
          mobileNumber: currentUser?.phoneNumber,
          firebaseId: currentUser?.uid,
          type: 'AFC'
        }
        try {
          await updateSyncUserDetails({
            firebaseId: currentUser?.uid,
            updatePayload,
            token: userToken
          }).unwrap()
        } catch (updateError) {
          setIsUserSynced(false)
          console.error('Error in update-user API:', updateError)
        }
      } else {
        setIsUserSynced(false)
      }
    }
  }

  const syncUserWithFirestore = async (currentUser: User) => {
    const db = getFirestore()
    const userDocRef = doc(db, 'users', currentUser.uid)
    const userDoc = await getDoc(userDocRef)

    const providerData = currentUser.providerData[0]
    const defaultUserDetails = {
      email: providerData?.email || '',
      fullName: providerData?.displayName || '',
      mobile: providerData?.phoneNumber || null,
      photoURL: providerData?.photoURL || null,
      providerId: providerData?.providerId || '',
      type: ['AFC']
    }

    if (!userDoc.exists()) {
      await setDoc(userDocRef, defaultUserDetails)
      return defaultUserDetails
    }
    const existingUserDetails = userDoc.data() || {}
    if (
      !existingUserDetails.type ||
      !Array.isArray(existingUserDetails.type) ||
      !existingUserDetails.type.includes('AFC')
    ) {
      existingUserDetails.type = Array.isArray(existingUserDetails.type)
        ? [...existingUserDetails.type, 'AFC']
        : ['AFC']
      await setDoc(userDocRef, existingUserDetails, { merge: true })
    }
    return existingUserDetails
  }

  const login = useCallback(async () => {
    if (isLoggingIn.current || loading) return
    isLoggingIn.current = true
    setLoading(true)
    try {
      const provider = new GoogleAuthProvider()
      const result = await signInWithPopup(auth, provider)

      const userData = result?.user
      const accessToken = await userData.getIdToken(true)
      localStorage.setItem('token', accessToken)
      localStorage.setItem('user', JSON.stringify(userData))
      setToken(accessToken)
      setUser(userData)
      notifySuccess('Login Successful')
      await syncUserWithFirestore(result.user)

      if (token) await fetchUserDetails()
    } catch (error: any) {
      console.error('Login error:', error.message || error)
      notifyError('Login failed')
    } finally {
      isLoggingIn.current = false
      setLoading(false)
    }
  }, [fetchUserDetails, loading, token])

  const fetchUserRole = useCallback(() => {
    return role;
  }, [role]);

  const error = userDetailError ?? null;

  const value = useMemo(
    () => ({
      user,
      token,
      role,
      login,
      loginUserDetails,
      syncUserWithDatabase,
      logout,
      fetchUserDetails,
      errorCompanyDetails,
      isUserSynced,
      refreshToken,
      loading,
      setIsSubsId,
      loadingRole,
      isSubsId,
      isCompanyVerified,
      openEditProfile,
      setopenEditProfile,
      fetchUserRole,
      error
    }),
    [
      token,
      user,
      role,
      login,
      logout,
      loading,
      loginUserDetails,
      errorCompanyDetails,
      syncUserWithDatabase,
      isUserSynced,
      loadingRole,
      fetchUserDetails,
      refreshToken,
      isSubsId,
      isCompanyVerified,
      setIsSubsId,
      openEditProfile,
      setopenEditProfile,
      fetchUserRole,
      error
    ]
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
